/**
 * TrendChartV2 Component
 *
 * Displays SPLH trend line chart with dual series for the SPLH Performance Dashboard V2.
 * Shows current period vs comparison period SPLH values.
 *
 * Features:
 * - Dual line chart using Chart.js with react-chartjs-2
 * - Current period line (solid purple)
 * - Comparison period line (dashed gray)
 * - Optional target line (red dashed)
 * - Interactive tooltips with daily details
 * - Responsive sizing
 * - Loading state with skeleton placeholder
 *
 * @module pages/common/dashboards/SPLHDashboard/components/TrendChartV2
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Box, Typography, Skeleton } from '@mui/material';
import { format, parseISO, eachDayOfInterval, differenceInDays } from 'date-fns';
import { formatSPLH, formatCurrency, formatNumber } from '../../utils/formatUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * Formats a date string for display on the chart axis
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @param {number} totalDays - Total number of days in the date range
 * @returns {string} Formatted date
 */
const formatDateLabel = (dateString, totalDays) => {
  try {
    const date = parseISO(dateString);
    if (totalDays <= 7) {
      return format(date, 'EEE d');
    }
    if (totalDays <= 14) {
      return format(date, 'MMM d');
    }
    if (totalDays <= 31) {
      return format(date, 'd');
    }
    return format(date, 'M/d');
  } catch {
    return dateString;
  }
};

/**
 * Formats a date string for tooltip display
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Formatted date
 */
const formatDateTooltip = (dateString) => {
  try {
    return format(parseISO(dateString), 'EEEE, MMM d, yyyy');
  } catch {
    return dateString;
  }
};

/**
 * TrendChartV2 Component
 *
 * @param {Object} props - Component props
 * @param {Array} props.trendData - Array of V2 trend points from backend
 * @param {Object} props.currentRange - Current period date range
 * @param {Object} props.comparisonRange - Comparison period date range
 * @param {number} [props.targetSPLH] - Optional target SPLH for reference line
 * @param {boolean} [props.loading=false] - Whether data is loading
 * @param {Object} props.colors - Color configuration from config
 * @param {string} [props.height='300px'] - Chart height
 * @returns {React.Component} TrendChartV2 component
 */
const TrendChartV2 = ({
  trendData = [],
  currentRange,
  comparisonRange,
  targetSPLH,
  loading = false,
  colors,
  height = '300px',
}) => {
  // Default colors if not provided
  const chartColors = colors || {
    current: '#7C3AED',
    comparison: '#9CA3AF',
    target: '#EF4444',
  };

  // Build complete date range with all days filled in
  const { filledData, totalDays } = useMemo(() => {
    if (!currentRange?.startDate || !currentRange?.endDate) {
      return {
        filledData: trendData || [],
        totalDays: trendData?.length || 0,
      };
    }

    try {
      const start = parseISO(currentRange.startDate);
      const end = parseISO(currentRange.endDate);
      const days = differenceInDays(end, start) + 1;

      const allDates = eachDayOfInterval({ start, end });

      // Create a map of existing data by date
      const dataByDate = new Map();
      if (trendData) {
        trendData.forEach((point) => {
          dataByDate.set(point.date, point);
        });
      }

      // Fill in all dates
      const filled = allDates.map((date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const existingData = dataByDate.get(dateStr);
        return existingData || {
          date: dateStr,
          currentSPLH: null,
          comparisonSPLH: null,
          deltaPct: null,
          currentSales: null,
          comparisonSales: null,
          currentHours: null,
          comparisonHours: null,
        };
      });

      return { filledData: filled, totalDays: days };
    } catch {
      return {
        filledData: trendData || [],
        totalDays: trendData?.length || 0,
      };
    }
  }, [currentRange, trendData]);

  // Process data for Chart.js and calculate y-axis bounds
  const { chartData, yAxisBounds } = useMemo(() => {
    if (!filledData || filledData.length === 0) {
      return { chartData: { labels: [], datasets: [] }, yAxisBounds: { min: undefined, max: undefined } };
    }

    const labels = filledData.map((point) => formatDateLabel(point.date, totalDays));
    const currentValues = filledData.map((point) => point.currentSPLH);
    const comparisonValues = filledData.map((point) => point.comparisonSPLH);

    // Calculate y-axis bounds with padding to ensure lines sit in middle band (25-75% of chart height)
    // Algorithm ensures lines never hug top/bottom and differences are easy to see
    const MIN_RANGE = 0.20;           // Minimum effective range to prevent flat charts
    const BOTTOM_PADDING_FACTOR = 0.25;
    const TOP_PADDING_FACTOR = 0.25;
    const MIN_ABSOLUTE_PADDING = 0.10;
    const TICK_STEP = 0.1;            // Round to nearest 0.1 for nice labels

    // Include both current and comparison values
    const allValues = [...currentValues, ...comparisonValues].filter(
      (v) => v !== null && v !== undefined && v > 0
    );

    let yMin;
    let yMax;

    if (allValues.length > 0) {
      // Step 1: Get raw data range (include target if provided)
      let dataMin = Math.min(...allValues);
      let dataMax = Math.max(...allValues);

      // Include target line in range calculation if present
      if (targetSPLH && targetSPLH > 0) {
        dataMin = Math.min(dataMin, targetSPLH);
        dataMax = Math.max(dataMax, targetSPLH);
      }

      const rawRange = dataMax - dataMin;

      // Step 2: Protect against tiny or flat ranges
      const range = rawRange < MIN_RANGE ? MIN_RANGE : rawRange;

      // Step 3: Compute padding for top and bottom
      const bottomPadding = Math.max(range * BOTTOM_PADDING_FACTOR, MIN_ABSOLUTE_PADDING);
      const topPadding = Math.max(range * TOP_PADDING_FACTOR, MIN_ABSOLUTE_PADDING);

      // Step 4: Compute Y-axis limits
      yMin = dataMin - bottomPadding;
      yMax = dataMax + topPadding;

      // Step 5: Clamp to zero (SPLH can't be negative)
      if (yMin < 0) {
        yMin = 0;
      }

      // Step 6: Round to nice tick values
      yMin = Math.floor(yMin / TICK_STEP) * TICK_STEP;
      yMax = Math.ceil(yMax / TICK_STEP) * TICK_STEP;
    }

    const datasets = [
      {
        label: 'Current Period',
        data: currentValues,
        borderColor: chartColors.current,
        backgroundColor: `${chartColors.current}20`,
        borderWidth: 3,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointBackgroundColor: chartColors.current,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.3,
        fill: true,
        spanGaps: true,
      },
      {
        label: 'Comparison Period',
        data: comparisonValues,
        borderColor: chartColors.comparison,
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBackgroundColor: chartColors.comparison,
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        tension: 0.3,
        fill: false,
        spanGaps: true,
      },
    ];

    // Add target line if targetSPLH is provided
    if (targetSPLH && targetSPLH > 0) {
      datasets.push({
        label: 'Target',
        data: Array(labels.length).fill(targetSPLH),
        borderColor: chartColors.target,
        borderWidth: 2,
        borderDash: [8, 4],
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        fill: false,
      });
    }

    return {
      chartData: { labels, datasets },
      yAxisBounds: { min: yMin, max: yMax },
    };
  }, [filledData, totalDays, targetSPLH, chartColors]);

  // Chart options
  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            size: 12,
            family: 'Roboto, sans-serif',
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1a1a1a',
        bodyColor: '#666',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        displayColors: true,
        filter: (tooltipItem) => tooltipItem.parsed.y !== null,
        callbacks: {
          title: (tooltipItems) => {
            if (tooltipItems.length > 0) {
              const index = tooltipItems[0].dataIndex;
              const point = filledData[index];
              return point?.date ? formatDateTooltip(point.date) : '';
            }
            return '';
          },
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;

            if (value === null) {
              return ` ${label}: No data`;
            }
            return ` ${label}: ${formatSPLH(value)}`;
          },
          afterBody: (tooltipItems) => {
            if (tooltipItems.length > 0) {
              const index = tooltipItems[0].dataIndex;
              const point = filledData[index];

              if (point && point.currentSPLH !== null && point.comparisonSPLH !== null) {
                const lines = [''];
                const deltaPct = point.deltaPct;
                if (deltaPct !== null && deltaPct !== undefined) {
                  const sign = deltaPct >= 0 ? '+' : '';
                  lines.push(`Change: ${sign}${deltaPct.toFixed(1)}%`);
                }
                if (point.currentSales !== null) {
                  lines.push(`Current Sales: ${formatCurrency(point.currentSales, { minimumFractionDigits: 0 })}`);
                }
                if (point.currentHours !== null) {
                  lines.push(`Current Hours: ${formatNumber(point.currentHours, { decimalPlaces: 1 })}`);
                }
                return lines.length > 1 ? lines : [];
              }
            }
            return [];
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#9ca2b7',
          font: {
            size: 11,
            family: 'Roboto, sans-serif',
          },
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: false,
        min: yAxisBounds.min,
        max: yAxisBounds.max,
        grid: {
          color: '#f0f0f0',
          drawBorder: false,
        },
        ticks: {
          color: '#9ca2b7',
          font: {
            size: 11,
            family: 'Roboto, sans-serif',
          },
          callback: (value) => `$${value.toFixed(2)}`,
          padding: 8,
        },
      },
    },
  }), [filledData, yAxisBounds]);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ height, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Skeleton variant="text" width={120} height={24} />
          <Skeleton variant="text" width={200} height={24} />
        </Box>
        <Skeleton variant="rectangular" width="100%" height={`calc(${height} - 60px)`} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  // Empty data state
  if ((!trendData || trendData.length === 0) && !currentRange?.startDate) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fafafa',
          borderRadius: 1,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No trend data available
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Select a date range to view SPLH trends
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height, position: 'relative' }}>
      <Line data={chartData} options={options} />
    </Box>
  );
};

TrendChartV2.propTypes = {
  /** Array of V2 trend points from backend */
  trendData: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      currentSPLH: PropTypes.number,
      comparisonSPLH: PropTypes.number,
      deltaPct: PropTypes.number,
      currentSales: PropTypes.number,
      comparisonSales: PropTypes.number,
      currentHours: PropTypes.number,
      comparisonHours: PropTypes.number,
    })
  ),
  /** Current period date range */
  currentRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  /** Comparison period date range */
  comparisonRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  /** Optional target SPLH for reference line */
  targetSPLH: PropTypes.number,
  /** Whether data is loading */
  loading: PropTypes.bool,
  /** Color configuration */
  colors: PropTypes.shape({
    current: PropTypes.string,
    comparison: PropTypes.string,
    target: PropTypes.string,
  }),
  /** Chart height */
  height: PropTypes.string,
};

export default TrendChartV2;
